import type { OperatorPermission } from "@strust/contracts";
import { EmptyState, ErrorState, Skeleton } from "@strust/ui";
import type { ReactNode } from "react";
import { useIntl } from "react-intl";
import { describeError } from "../features/access/model/error-message.ts";
import { appMessages } from "./i18n/messages.ts";
import { ApiRequestError } from "./api/client.ts";
import { accessMessages } from "./investigation-messages.ts";
import { usePermissions } from "./use-permissions.ts";

function NoPermission() {
  const intl = useIntl();
  return (
    <EmptyState
      title={intl.formatMessage(accessMessages.noPermissionTitle)}
      description={intl.formatMessage(accessMessages.noPermissionDescription)}
    />
  );
}

/**
 * Screen-level permission gate (no-permission state). It mirrors the backend check, which stays the
 * authority: an API `forbidden` is handled by `ApiProblem` as the same state.
 */
export function AccessGate({
  permission,
  children,
}: {
  readonly permission: OperatorPermission;
  readonly children: ReactNode;
}) {
  const permissions = usePermissions();
  if (permissions.isLoading) {
    return (
      <main id="main-content" aria-busy="true">
        <Skeleton height="2rem" />
      </main>
    );
  }
  if (!permissions.can(permission)) {
    return (
      <main id="main-content">
        <NoPermission />
      </main>
    );
  }
  return <>{children}</>;
}

/**
 * A failed read: `forbidden` is the no-permission state, `not_found` (also how another operator's
 * data answers) the not-found state, anything else an error with a retry.
 */
export function ApiProblem({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry: () => void;
}) {
  const intl = useIntl();
  if (error instanceof ApiRequestError && error.code === "forbidden") return <NoPermission />;
  if (error instanceof ApiRequestError && error.code === "not_found") {
    return (
      <EmptyState
        title={intl.formatMessage(accessMessages.notFoundTitle)}
        description={intl.formatMessage(accessMessages.notFoundDescription)}
      />
    );
  }
  return (
    <ErrorState
      title={intl.formatMessage(accessMessages.loadErrorTitle)}
      retryLabel={intl.formatMessage(appMessages.errorRetry)}
      onRetry={onRetry}
    >
      <p>{describeError(intl, error)}</p>
    </ErrorState>
  );
}
